import { useState, useEffect, useCallback, useMemo } from "react";
import { Routes, Route, Link, useNavigate, useParams } from "react-router-dom";
import MainLayout from "@/components/layout/MainLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { NativeSelect } from "@/components/ui/native-select";
import { Separator } from "@/components/ui/separator";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader,
  AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import {
  DndContext, closestCorners, PointerSensor, useSensor, useSensors, DragOverlay, useDroppable,
} from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Plus, Search, Filter, Loader2, Phone, Mail, Calendar, DollarSign,
  User, ArrowLeft, Clock, MessageSquare, Eye, Edit, Trash2, X,
  AlertCircle, Target, ChevronDown, GripVertical, Building,
  Send, FileText
} from "lucide-react";
import { extractErrorMessage } from "@/lib/errorMessage";
import { FollowUpDialog } from "@/components/crm/CRMExtras";

const STAGES = [
  { id: "contato_cliente", label: "Contato com Cliente", color: "#3B82F6", bg: "bg-blue-50", border: "border-blue-200" },
  { id: "visita_tecnica", label: "Visita Tecnica", color: "#F59E0B", bg: "bg-amber-50", border: "border-amber-200" },
  { id: "propostas", label: "Propostas", color: "#10B981", bg: "bg-emerald-50", border: "border-emerald-200" },
  { id: "diagnostico", label: "Proposta Potencial", color: "#8B5CF6", bg: "bg-violet-50", border: "border-violet-200" },
  { id: "contrato", label: "Contrato", color: "#0A1628", bg: "bg-slate-50", border: "border-slate-300" },
  { id: "venda_fechada", label: "Venda Fechada", color: "#059669", bg: "bg-emerald-100/60", border: "border-emerald-300" },
  { id: "venda_perdida", label: "Venda Perdida", color: "#EF4444", bg: "bg-red-50", border: "border-red-200" },
];

const PRIORITY_COLORS = {
  baixa: "bg-slate-100 text-slate-600",
  media: "bg-blue-100 text-blue-700",
  alta: "bg-amber-100 text-amber-700",
  urgente: "bg-red-100 text-red-700",
};

const ORIGIN_LABELS = {
  indicacao: "Indicacao", site: "Site", telefone: "Telefone", whatsapp: "WhatsApp",
  email: "E-mail", redes_sociais: "Redes Sociais", visita: "Visita", outro: "Outro",
};

function maskPhone(v) {
  const d = v.replace(/\D/g, "");
  if (d.length <= 10) return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{4})(\d)/, "$1-$2");
  return d.replace(/(\d{2})(\d)/, "($1) $2").replace(/(\d{5})(\d)/, "$1-$2").slice(0, 15);
}

// ==================== LEAD CARD (Draggable) ====================
function LeadCard({ lead, onOpen }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: lead.id,
    data: { lead, etapa: lead.etapa },
  });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.4 : 1 };
  const isOverdue = lead.data_proxima_acao && new Date(lead.data_proxima_acao) < new Date();
  const slaClass = lead.sla_status === "vermelho" ? "sla-pulse-red" : lead.sla_status === "amarelo" ? "sla-pulse-yellow" : "";
  const slaTooltip = lead.sla_status && lead.sla_status !== "ok"
    ? `Parado ha ${lead.dias_parado} dia(s) nesta etapa`
    : (lead.dias_parado > 0 ? `${lead.dias_parado} dia(s) nesta etapa` : "");

  return (
    <div ref={setNodeRef} style={style} className="group" data-testid={`lead-card-${lead.id}`} title={slaTooltip}>
      <Card className={`border shadow-sm hover:shadow-md transition-all cursor-pointer ${slaClass || (isOverdue ? "border-red-300 bg-red-50/30" : "")}`} onClick={() => onOpen(lead)}>
        <CardContent className="p-3 space-y-2">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-1.5 flex-1 min-w-0">
              <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing p-0.5 -ml-1 opacity-0 group-hover:opacity-60 transition-opacity">
                <GripVertical className="h-3.5 w-3.5 text-slate-400" />
              </div>
              <p className="font-semibold text-sm text-[#0A1628] truncate">{lead.titulo}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0 ml-1">
              {lead.sla_status === "vermelho" && (
                <Badge className="bg-red-500 text-white text-[10px] gap-0.5" data-testid={`sla-badge-vermelho-${lead.id}`}>
                  <AlertCircle className="h-2.5 w-2.5" />{lead.dias_parado}d
                </Badge>
              )}
              {lead.sla_status === "amarelo" && (
                <Badge className="bg-amber-500 text-white text-[10px] gap-0.5" data-testid={`sla-badge-amarelo-${lead.id}`}>
                  <Clock className="h-2.5 w-2.5" />{lead.dias_parado}d
                </Badge>
              )}
              <Badge className={`${PRIORITY_COLORS[lead.prioridade] || PRIORITY_COLORS.media} text-[10px]`}>
                {lead.prioridade}
              </Badge>
            </div>
          </div>

          {lead.cliente_nome && (
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <Building className="h-3 w-3" />
              <span className="truncate">{lead.cliente_nome}</span>
            </div>
          )}

          {lead.valor_estimado > 0 && (
            <p className="text-sm font-mono font-medium text-emerald-600">
              R$ {Number(lead.valor_estimado).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
            </p>
          )}

          {!lead.data_proxima_acao && !["contrato", "venda_fechada", "venda_perdida"].includes(lead.etapa) && (
            <Badge variant="outline" className="text-[9px] border-amber-300 text-amber-700 bg-amber-50/60" data-testid={`badge-sem-acao-${lead.id}`}>
              sem próxima ação
            </Badge>
          )}

          <div className="flex items-center justify-between text-[10px] text-slate-400">
            <div className="flex items-center gap-2">
              {lead.responsavel_nome && (
                <span className="flex items-center gap-0.5"><User className="h-3 w-3" />{lead.responsavel_nome.split(" ")[0]}</span>
              )}
              {lead.origem && <span>{ORIGIN_LABELS[lead.origem] || lead.origem}</span>}
            </div>
            {lead.data_proxima_acao && (
              <span className={`flex items-center gap-0.5 ${isOverdue ? "text-red-500 font-medium" : ""}`}>
                <Clock className="h-3 w-3" />
                {new Date(lead.data_proxima_acao).toLocaleDateString("pt-BR")}
              </span>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

// ==================== KANBAN COLUMN ====================
function KanbanColumn({ stage, leads, onOpenLead, onAddLead }) {
  const { setNodeRef, isOver } = useDroppable({ id: stage.id, data: { etapa: stage.id } });
  const totalValue = leads.reduce((s, l) => s + (l.valor_estimado || 0), 0);

  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col min-w-[280px] max-w-[300px] rounded-xl transition-colors ${isOver ? "ring-2 ring-[#C9A84C]" : ""}`}
      data-testid={`column-${stage.id}`}
    >
      {/* Column Header */}
      <div className="p-3 rounded-t-xl" style={{ backgroundColor: stage.color + "12", borderBottom: `2px solid ${stage.color}` }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-3 w-3 rounded-full" style={{ backgroundColor: stage.color }} />
            <p className="text-sm font-bold" style={{ color: stage.color }}>{stage.label}</p>
            <Badge variant="outline" className="text-[10px] h-5">{leads.length}</Badge>
          </div>
          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => onAddLead(stage.id)} data-testid={`btn-add-${stage.id}`}>
            <Plus className="h-3.5 w-3.5" />
          </Button>
        </div>
        {totalValue > 0 && (
          <p className="text-xs font-mono text-slate-500 mt-1">
            R$ {totalValue.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
          </p>
        )}
      </div>

      {/* Cards Area */}
      <div className="flex-1 p-2 space-y-2 min-h-[200px] bg-slate-50/50 rounded-b-xl overflow-y-auto max-h-[calc(100vh-280px)]">
        <SortableContext items={leads.map(l => l.id)} strategy={verticalListSortingStrategy}>
          {leads.map(lead => (
            <LeadCard key={lead.id} lead={lead} onOpen={onOpenLead} />
          ))}
        </SortableContext>
        {leads.length === 0 && (
          <div className="flex items-center justify-center h-24 text-xs text-slate-400 border-2 border-dashed rounded-lg">
            Arraste um lead aqui
          </div>
        )}
      </div>
    </div>
  );
}

// ==================== CRM KANBAN ====================
function CRMKanban() {
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterPrioridade, setFilterPrioridade] = useState("");
  const [filterResponsavel, setFilterResponsavel] = useState("");
  const [showNewLead, setShowNewLead] = useState(false);
  const [newLeadEtapa, setNewLeadEtapa] = useState("contato_cliente");
  const [showLeadDetail, setShowLeadDetail] = useState(null);
  const [showLossReason, setShowLossReason] = useState(null);
  const [lossReason, setLossReason] = useState("");
  const [activeDrag, setActiveDrag] = useState(null);
  const [clientes, setClientes] = useState([]);
  const [funcionarios, setFuncionarios] = useState([]);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [newLeadForm, setNewLeadForm] = useState({
    titulo: "", cliente_id: "", cliente_nome: "", prioridade: "media",
    origem: "", origem_outro_descricao: "", responsavel_id: "", responsavel_nome: "",
    valor_estimado: "", descricao: "", telefone: "", email: "",
    proxima_acao: "", data_proxima_acao: "",
  });
  const [newActivity, setNewActivity] = useState({ tipo: "observacao", descricao: "" });
  const [savingLead, setSavingLead] = useState(false);
  const [showQuickCliente, setShowQuickCliente] = useState(false);
  const [quickCliente, setQuickCliente] = useState({ nome: "", documento: "", telefone: "", email: "" });
  const [savingQuickCliente, setSavingQuickCliente] = useState(false);
  const [followUp, setFollowUp] = useState(null); // {lead, etapaLabel}

  const token = sessionStorage.getItem("luvcorp_token");
  const headers = { Authorization: `Bearer ${token}` };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  useEffect(() => { fetchLeads(); fetchClientes(); fetchFuncionarios(); }, []);

  const fetchLeads = async () => {
    try { const res = await axios.get(`${API}/crm/leads`, { headers }); setLeads(res.data); }
    catch { toast.error("Erro ao carregar leads"); }
    finally { setLoading(false); }
  };

  const fetchClientes = async () => {
    try { const res = await axios.get(`${API}/clientes-fornecedores`, { headers }); setClientes(res.data); } catch {}
  };

  const fetchFuncionarios = async () => {
    try { const res = await axios.get(`${API}/funcionarios`, { headers }); setFuncionarios(res.data); } catch {}
  };

  const leadsByStage = useMemo(() => {
    const filtered = leads.filter(l => {
      if (searchTerm && !l.titulo?.toLowerCase().includes(searchTerm.toLowerCase()) && !l.cliente_nome?.toLowerCase().includes(searchTerm.toLowerCase())) return false;
      if (filterPrioridade && l.prioridade !== filterPrioridade) return false;
      if (filterResponsavel && l.responsavel_id !== filterResponsavel) return false;
      return true;
    });
    const grouped = {};
    STAGES.forEach(s => { grouped[s.id] = filtered.filter(l => l.etapa === s.id).sort((a, b) => (a.posicao || 0) - (b.posicao || 0)); });
    return grouped;
  }, [leads, searchTerm, filterPrioridade, filterResponsavel]);

  const handleDragStart = (event) => {
    const lead = leads.find(l => l.id === event.active.id);
    setActiveDrag(lead);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
    setActiveDrag(null);
    if (!over) return;

    const leadId = active.id;
    const lead = leads.find(l => l.id === leadId);
    if (!lead) return;

    // Determine target stage
    let targetEtapa = over.data?.current?.etapa;
    if (!targetEtapa) {
      const overLead = leads.find(l => l.id === over.id);
      targetEtapa = overLead?.etapa;
    }
    if (!targetEtapa || targetEtapa === lead.etapa) return;

    // Check if moving to venda_perdida - require reason
    if (targetEtapa === "venda_perdida") {
      setShowLossReason({ leadId, targetEtapa });
      return;
    }

    // Move lead
    try {
      await axios.put(`${API}/crm/leads/${leadId}/mover-etapa`, { etapa: targetEtapa, posicao: 0 }, { headers });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, etapa: targetEtapa, posicao: 0 } : l));
      const stageLabel = STAGES.find(s => s.id === targetEtapa)?.label;
      toast.success(`Lead movido para ${stageLabel}`);
      // Follow-up inteligente: pergunta a proxima acao (etapas abertas)
      if (!["contrato", "venda_fechada", "venda_perdida"].includes(targetEtapa)) {
        setFollowUp({ lead: { ...lead, etapa: targetEtapa }, etapaLabel: stageLabel });
      }
    } catch { toast.error("Erro ao mover lead"); }
  };

  const handleFollowUpConfirm = async (acao, dataAcao) => {
    const leadId = followUp.lead.id;
    try {
      await axios.put(`${API}/crm/leads/${leadId}`, { proxima_acao: acao, data_proxima_acao: dataAcao }, { headers });
      setLeads(prev => prev.map(l => l.id === leadId ? { ...l, proxima_acao: acao, data_proxima_acao: dataAcao } : l));
      toast.success("Follow-up agendado");
      setFollowUp(null);
    } catch (err) { toast.error(extractErrorMessage(err, "Erro ao agendar follow-up")); }
  };

  const handleLossConfirm = async () => {
    if (!lossReason.trim()) { toast.error("Informe o motivo da perda"); return; }
    try {
      await axios.put(`${API}/crm/leads/${showLossReason.leadId}/mover-etapa`, {
        etapa: "venda_perdida", posicao: 0, motivo_perda: lossReason,
      }, { headers });
      setLeads(prev => prev.map(l => l.id === showLossReason.leadId ? { ...l, etapa: "venda_perdida", motivo_perda: lossReason } : l));
      toast.success("Lead movido para Venda Perdida");
      setShowLossReason(null);
      setLossReason("");
    } catch { toast.error("Erro"); }
  };

  const handleCreateLead = async () => {
    if (!newLeadForm.titulo) { toast.error("Titulo obrigatorio"); return; }
    setSavingLead(true);
    try {
      const payload = {
        ...newLeadForm,
        etapa: newLeadEtapa,
        valor_estimado: newLeadForm.valor_estimado ? parseFloat(newLeadForm.valor_estimado) : null,
      };
      if (payload.origem !== "outro") payload.origem_outro_descricao = "";
      const res = await axios.post(`${API}/crm/leads`, payload, { headers });
      setLeads(prev => [...prev, res.data]);
      setShowNewLead(false);
      resetNewLeadForm();
      toast.success("Lead criado!");
    } catch (err) { toast.error(extractErrorMessage(err, "Erro")); }
    finally { setSavingLead(false); }
  };

  const resetNewLeadForm = () => setNewLeadForm({
    titulo: "", cliente_id: "", cliente_nome: "", prioridade: "media",
    origem: "", origem_outro_descricao: "", responsavel_id: "", responsavel_nome: "",
    valor_estimado: "", descricao: "", telefone: "", email: "",
    proxima_acao: "", data_proxima_acao: "",
  });

  const handleClienteSelect = (clienteId) => {
    const c = clientes.find(x => x.id === clienteId);
    if (c) {
      setNewLeadForm(p => ({
        ...p, cliente_id: c.id, cliente_nome: c.nome,
        telefone: c.telefone || p.telefone,
        email: c.email || p.email,
        endereco: c.endereco ? `${c.endereco}${c.numero ? `, ${c.numero}` : ""} - ${c.cidade || ""}/${c.estado || ""}` : p.endereco,
      }));
    }
  };

  const handleQuickClienteSave = async () => {
    if (!quickCliente.nome.trim()) { toast.error("Nome obrigatorio"); return; }
    setSavingQuickCliente(true);
    try {
      // Detecta tipo_pessoa pelo tamanho do documento (CPF=11, CNPJ=14)
      const docDigits = (quickCliente.documento || "").replace(/\D/g, "");
      const tipo_pessoa = docDigits.length === 14 ? "juridica" : "fisica";
      const payload = {
        tipo_pessoa,
        tipo_cadastro: "cliente",
        nome: quickCliente.nome.trim(),
        documento: quickCliente.documento || "",
        telefone_whatsapp_1: quickCliente.telefone || "",
        email: quickCliente.email || null,
      };
      const res = await axios.post(`${API}/clientes-fornecedores`, payload, { headers });
      const novo = res.data;
      setClientes(prev => [...prev, novo]);
      // Auto-vincula ao lead em criacao
      setNewLeadForm(p => ({
        ...p,
        cliente_id: novo.id,
        cliente_nome: novo.nome,
        telefone: novo.telefone_whatsapp_1 || p.telefone,
        email: novo.email || p.email,
      }));
      setShowQuickCliente(false);
      setQuickCliente({ nome: "", documento: "", telefone: "", email: "" });
      toast.success("Cliente cadastrado e vinculado!");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao cadastrar cliente"));
    } finally {
      setSavingQuickCliente(false);
    }
  };

  const handleAddActivity = async () => {
    if (!newActivity.descricao.trim() || !showLeadDetail) return;
    try {
      await axios.post(`${API}/crm/leads/${showLeadDetail.id}/atividade`, newActivity, { headers });
      // Refresh lead detail
      const res = await axios.get(`${API}/crm/leads/${showLeadDetail.id}`, { headers });
      setShowLeadDetail(res.data);
      setLeads(prev => prev.map(l => l.id === showLeadDetail.id ? { ...res.data } : l));
      setNewActivity({ tipo: "observacao", descricao: "" });
      toast.success("Atividade registrada!");
    } catch { toast.error("Erro"); }
  };

  const handleDeleteLead = async (id) => {
    try {
      await axios.delete(`${API}/crm/leads/${id}`, { headers });
      setLeads(prev => prev.filter(l => l.id !== id));
      setShowLeadDetail(null);
      toast.success("Lead excluido");
    } catch { toast.error("Erro"); }
  };

  const onAddLead = (etapa) => {
    setNewLeadEtapa(etapa);
    resetNewLeadForm();
    setShowNewLead(true);
  };

  const openLeadDetail = async (lead) => {
    try {
      const res = await axios.get(`${API}/crm/leads/${lead.id}`, { headers });
      setShowLeadDetail(res.data);
      setEditMode(false);
      setEditForm(null);
    } catch { setShowLeadDetail(lead); setEditMode(false); setEditForm(null); }
  };

  const startEdit = (lead) => {
    setEditForm({
      titulo: lead.titulo || "",
      cliente_id: lead.cliente_id || "",
      cliente_nome: lead.cliente_nome || "",
      prioridade: lead.prioridade || "media",
      origem: lead.origem || "",
      origem_outro_descricao: lead.origem_outro_descricao || "",
      responsavel_id: lead.responsavel_id || "",
      responsavel_nome: lead.responsavel_nome || "",
      valor_estimado: lead.valor_estimado != null ? String(lead.valor_estimado) : "",
      descricao: lead.descricao || "",
      telefone: lead.telefone || "",
      email: lead.email || "",
      endereco: lead.endereco || "",
      proxima_acao: lead.proxima_acao || "",
      data_proxima_acao: lead.data_proxima_acao ? lead.data_proxima_acao.slice(0, 10) : "",
    });
    setEditMode(true);
  };

  const handleSaveEdit = async () => {
    if (!showLeadDetail || !editForm) return;
    if (!editForm.titulo.trim()) { toast.error("Titulo obrigatorio"); return; }
    setSavingEdit(true);
    try {
      const payload = {
        ...editForm,
        valor_estimado: editForm.valor_estimado === "" || editForm.valor_estimado == null
          ? null
          : parseFloat(editForm.valor_estimado),
        data_proxima_acao: editForm.data_proxima_acao || null,
      };
      // Limpa campo de descrição de outro se a origem não for "outro"
      if (payload.origem !== "outro") payload.origem_outro_descricao = "";
      await axios.put(`${API}/crm/leads/${showLeadDetail.id}`, payload, { headers });
      const res = await axios.get(`${API}/crm/leads/${showLeadDetail.id}`, { headers });
      setShowLeadDetail(res.data);
      setLeads(prev => prev.map(l => l.id === showLeadDetail.id ? { ...l, ...res.data } : l));
      setEditMode(false);
      toast.success("Lead atualizado!");
    } catch (err) {
      toast.error(extractErrorMessage(err, "Erro ao salvar"));
    } finally {
      setSavingEdit(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-[#0A1628]" /></div>;

  return (
    <div className="space-y-4" data-testid="crm-kanban">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-heading font-bold text-[#0A1628]">CRM Comercial</h1>
          <p className="text-sm text-slate-500">{leads.length} leads no pipeline</p>
        </div>
        <div className="flex gap-2">
          <Link to="/comercial/dashboard"><Button variant="outline" size="sm" data-testid="btn-dashboard"><Target className="h-4 w-4 mr-1" />Dashboard</Button></Link>
          <Button size="sm" className="bg-[#0A1628]" onClick={() => onAddLead("contato_cliente")} data-testid="btn-novo-lead"><Plus className="h-4 w-4 mr-1" />Novo Lead</Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px] max-w-xs">
          <Search className="absolute left-2.5 top-2 h-4 w-4 text-slate-400" />
          <Input placeholder="Buscar leads..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-8 h-9 text-sm" data-testid="search-leads" />
        </div>
        <NativeSelect value={filterPrioridade} onValueChange={setFilterPrioridade} options={[{ value: "", label: "Prioridade" }, { value: "baixa", label: "Baixa" }, { value: "media", label: "Media" }, { value: "alta", label: "Alta" }, { value: "urgente", label: "Urgente" }]} className="h-9 w-32 text-sm" />
        <NativeSelect value={filterResponsavel} onValueChange={setFilterResponsavel} options={[{ value: "", label: "Responsavel" }, ...funcionarios.map(f => ({ value: f.id, label: f.nome }))]} className="h-9 w-40 text-sm" />
        {(searchTerm || filterPrioridade || filterResponsavel) && (
          <Button variant="ghost" size="sm" onClick={() => { setSearchTerm(""); setFilterPrioridade(""); setFilterResponsavel(""); }}><X className="h-3 w-3 mr-1" />Limpar</Button>
        )}
      </div>

      {/* Kanban Board */}
      <DndContext sensors={sensors} collisionDetection={closestCorners} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <div className="flex gap-4 overflow-x-auto pb-4" style={{ minHeight: "calc(100vh - 260px)" }}>
          {STAGES.map(stage => (
            <KanbanColumn key={stage.id} stage={stage} leads={leadsByStage[stage.id] || []} onOpenLead={openLeadDetail} onAddLead={onAddLead} />
          ))}
        </div>
        <DragOverlay>
          {activeDrag && (
            <div className="w-[280px] opacity-90 rotate-2">
              <Card className="border shadow-lg">
                <CardContent className="p-3">
                  <p className="font-semibold text-sm">{activeDrag.titulo}</p>
                  {activeDrag.cliente_nome && <p className="text-xs text-slate-500">{activeDrag.cliente_nome}</p>}
                </CardContent>
              </Card>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* ========== DIALOG: NEW LEAD ========== */}
      <Dialog open={showNewLead} onOpenChange={setShowNewLead}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-[#0A1628]">Novo Lead</DialogTitle>
            <DialogDescription>Etapa: {STAGES.find(s => s.id === newLeadEtapa)?.label}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Titulo *</Label><Input value={newLeadForm.titulo} onChange={e => setNewLeadForm(p => ({ ...p, titulo: e.target.value }))} placeholder="Ex: Reforma Predio Centro" data-testid="input-titulo-lead" /></div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Cliente</Label>
                <Button type="button" variant="ghost" size="sm" className="h-6 text-xs gap-1 text-[#0A1628]" onClick={() => setShowQuickCliente(true)} data-testid="btn-quick-novo-cliente">
                  <Plus className="h-3 w-3" /> Novo cliente
                </Button>
              </div>
              <NativeSelect value={newLeadForm.cliente_id} onValueChange={handleClienteSelect} options={[{ value: "", label: "Buscar cliente cadastrado..." }, ...clientes.map(c => ({ value: c.id, label: `${c.nome} - ${c.documento || ""}` }))]} data-testid="select-cliente" />
              {newLeadForm.cliente_nome && <p className="text-xs text-emerald-600" data-testid="cliente-autovinculado-text">Vinculado: {newLeadForm.cliente_nome}</p>}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Prioridade</Label>
                <NativeSelect value={newLeadForm.prioridade} onValueChange={v => setNewLeadForm(p => ({ ...p, prioridade: v }))} options={[{ value: "baixa", label: "Baixa" }, { value: "media", label: "Media" }, { value: "alta", label: "Alta" }, { value: "urgente", label: "Urgente" }]} />
              </div>
              <div className="space-y-2"><Label>Origem</Label>
                <NativeSelect value={newLeadForm.origem} onValueChange={v => setNewLeadForm(p => ({ ...p, origem: v, origem_outro_descricao: v === "outro" ? p.origem_outro_descricao : "" }))} options={[{ value: "", label: "Selecione..." }, { value: "indicacao", label: "Indicacao" }, { value: "site", label: "Site" }, { value: "telefone", label: "Telefone" }, { value: "whatsapp", label: "WhatsApp" }, { value: "email", label: "E-mail" }, { value: "redes_sociais", label: "Redes Sociais" }, { value: "visita", label: "Visita" }, { value: "outro", label: "Outro" }]} />
              </div>
            </div>

            {newLeadForm.origem === "outro" && (
              <div className="space-y-2">
                <Label>Descreva a origem *</Label>
                <Input
                  value={newLeadForm.origem_outro_descricao}
                  onChange={e => setNewLeadForm(p => ({ ...p, origem_outro_descricao: e.target.value }))}
                  placeholder="Ex: Evento setorial, parceria com fornecedor X..."
                  data-testid="input-origem-outro-novo"
                />
              </div>
            )}

            <div className="space-y-2"><Label>Responsavel</Label>
              <NativeSelect value={newLeadForm.responsavel_id} onValueChange={v => { const f = funcionarios.find(x => x.id === v); setNewLeadForm(p => ({ ...p, responsavel_id: v, responsavel_nome: f?.nome || "" })); }} options={[{ value: "", label: "Selecione..." }, ...funcionarios.map(f => ({ value: f.id, label: f.nome }))]} />
            </div>

            <div className="space-y-2"><Label>Valor Estimado (R$)</Label><Input type="number" step="0.01" value={newLeadForm.valor_estimado} onChange={e => setNewLeadForm(p => ({ ...p, valor_estimado: e.target.value }))} placeholder="0,00" /></div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Telefone</Label><Input value={newLeadForm.telefone} onChange={e => setNewLeadForm(p => ({ ...p, telefone: maskPhone(e.target.value) }))} placeholder="(00) 00000-0000" /></div>
              <div className="space-y-2"><Label>E-mail</Label><Input type="email" value={newLeadForm.email} onChange={e => setNewLeadForm(p => ({ ...p, email: e.target.value }))} /></div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Proxima Acao</Label><Input value={newLeadForm.proxima_acao} onChange={e => setNewLeadForm(p => ({ ...p, proxima_acao: e.target.value }))} placeholder="Ex: Ligar para agendar" /></div>
              <div className="space-y-2"><Label>Data Proxima Acao</Label><Input type="date" value={newLeadForm.data_proxima_acao} onChange={e => setNewLeadForm(p => ({ ...p, data_proxima_acao: e.target.value }))} /></div>
            </div>

            <div className="space-y-2"><Label>Descricao</Label><Textarea value={newLeadForm.descricao} onChange={e => setNewLeadForm(p => ({ ...p, descricao: e.target.value }))} rows={3} placeholder="Detalhes do lead..." /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewLead(false)}>Cancelar</Button>
            <Button onClick={handleCreateLead} disabled={savingLead} className="bg-[#0A1628]" data-testid="btn-criar-lead">{savingLead ? "Criando..." : "Criar Lead"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== DIALOG: QUICK NOVO CLIENTE ========== */}
      <Dialog open={showQuickCliente} onOpenChange={setShowQuickCliente}>
        <DialogContent className="max-w-md" data-testid="dialog-quick-cliente">
          <DialogHeader>
            <DialogTitle className="text-[#0A1628] flex items-center gap-2"><Building className="h-5 w-5" />Novo Cliente (Rapido)</DialogTitle>
            <DialogDescription>Cadastra cliente sem sair do CRM. Sera vinculado ao lead automaticamente.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-2"><Label>Nome / Razao Social *</Label><Input value={quickCliente.nome} onChange={e => setQuickCliente(p => ({ ...p, nome: e.target.value }))} placeholder="Ex: Joao Silva ou Empresa LTDA" autoFocus data-testid="input-quick-nome" /></div>
            <div className="space-y-2"><Label>CPF / CNPJ</Label><Input value={quickCliente.documento} onChange={e => setQuickCliente(p => ({ ...p, documento: e.target.value }))} placeholder="000.000.000-00 ou 00.000.000/0000-00" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>Telefone</Label><Input value={quickCliente.telefone} onChange={e => setQuickCliente(p => ({ ...p, telefone: maskPhone(e.target.value) }))} placeholder="(00) 00000-0000" /></div>
              <div className="space-y-2"><Label>E-mail</Label><Input type="email" value={quickCliente.email} onChange={e => setQuickCliente(p => ({ ...p, email: e.target.value }))} placeholder="cliente@email.com" /></div>
            </div>
            <p className="text-xs text-slate-500">Para um cadastro completo, acesse Cadastros &rarr; Clientes apos criar este lead.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowQuickCliente(false)}>Cancelar</Button>
            <Button onClick={handleQuickClienteSave} disabled={savingQuickCliente} className="bg-[#0A1628]" data-testid="btn-salvar-quick-cliente">
              {savingQuickCliente ? "Salvando..." : "Salvar e vincular"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== DIALOG: LOSS REASON ========== */}
      <Dialog open={!!showLossReason} onOpenChange={() => { setShowLossReason(null); setLossReason(""); }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="text-red-600 flex items-center gap-2"><AlertCircle className="h-5 w-5" />Motivo da Perda</DialogTitle><DialogDescription>Informe o motivo pelo qual esta venda foi perdida.</DialogDescription></DialogHeader>
          <div className="space-y-3 py-2">
            <NativeSelect value={lossReason} onValueChange={setLossReason} options={[
              { value: "", label: "Selecione o motivo..." },
              { value: "Preco acima do mercado", label: "Preco acima do mercado" },
              { value: "Cliente escolheu concorrente", label: "Cliente escolheu concorrente" },
              { value: "Prazo nao atende", label: "Prazo nao atende" },
              { value: "Cliente desistiu da reforma", label: "Cliente desistiu da reforma" },
              { value: "Sem orcamento disponivel", label: "Sem orcamento disponivel" },
              { value: "Escopo incompativel", label: "Escopo incompativel" },
              { value: "Falta de retorno do cliente", label: "Falta de retorno do cliente" },
              { value: "Outro", label: "Outro" },
            ]} data-testid="select-motivo-perda" />
            {lossReason === "Outro" && <Input placeholder="Descreva o motivo..." onChange={e => setLossReason(e.target.value)} />}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowLossReason(null); setLossReason(""); }}>Cancelar</Button>
            <Button onClick={handleLossConfirm} className="bg-red-600 hover:bg-red-700" data-testid="btn-confirmar-perda">Confirmar Perda</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ========== DIALOG: FOLLOW-UP AO MOVER ETAPA ========== */}
      <FollowUpDialog data={followUp} onClose={() => setFollowUp(null)} onConfirm={handleFollowUpConfirm} />

      {/* ========== DIALOG: LEAD DETAIL ========== */}
      <Dialog open={!!showLeadDetail} onOpenChange={() => { setShowLeadDetail(null); setEditMode(false); setEditForm(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {showLeadDetail && (
            <>
              <DialogHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <DialogTitle className="text-[#0A1628] text-lg">{showLeadDetail.titulo}</DialogTitle>
                    <DialogDescription asChild>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge style={{ backgroundColor: STAGES.find(s => s.id === showLeadDetail.etapa)?.color, color: "white" }}>
                          {STAGES.find(s => s.id === showLeadDetail.etapa)?.label}
                        </Badge>
                        <Badge className={PRIORITY_COLORS[showLeadDetail.prioridade]}>{showLeadDetail.prioridade}</Badge>
                      </div>
                    </DialogDescription>
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant={editMode ? "default" : "outline"}
                      size="sm"
                      className={editMode ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
                      onClick={() => editMode ? handleSaveEdit() : startEdit(showLeadDetail)}
                      disabled={savingEdit}
                      data-testid="btn-edit-lead"
                    >
                      {editMode ? (savingEdit ? "Salvando..." : "Salvar") : <><Edit className="h-3.5 w-3.5 mr-1" />Editar</>}
                    </Button>
                    {editMode && (
                      <Button variant="ghost" size="sm" onClick={() => { setEditMode(false); setEditForm(null); }} data-testid="btn-cancel-edit-lead">Cancelar</Button>
                    )}
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-red-500"><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Excluir lead?</AlertDialogTitle><AlertDialogDescription>Esta acao nao pode ser desfeita.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Cancelar</AlertDialogCancel><AlertDialogAction onClick={() => handleDeleteLead(showLeadDetail.id)} className="bg-red-600">Excluir</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-4 py-2">
                {/* Lead Info - View OR Edit Mode */}
                {!editMode ? (
                  <>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      {showLeadDetail.cliente_nome && <div><p className="text-xs text-slate-400">Cliente</p><p className="font-medium">{showLeadDetail.cliente_nome}</p></div>}
                      {showLeadDetail.responsavel_nome && <div><p className="text-xs text-slate-400">Responsavel</p><p className="font-medium">{showLeadDetail.responsavel_nome}</p></div>}
                      {showLeadDetail.valor_estimado > 0 && <div><p className="text-xs text-slate-400">Valor Estimado</p><p className="font-medium text-emerald-600" data-testid="lead-valor-estimado">R$ {Number(showLeadDetail.valor_estimado).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</p></div>}
                      {showLeadDetail.origem && <div><p className="text-xs text-slate-400">Origem</p><p className="font-medium">{ORIGIN_LABELS[showLeadDetail.origem]}{showLeadDetail.origem === "outro" && showLeadDetail.origem_outro_descricao ? ` - ${showLeadDetail.origem_outro_descricao}` : ""}</p></div>}
                      {showLeadDetail.telefone && <div><p className="text-xs text-slate-400">Telefone</p><p className="font-medium">{showLeadDetail.telefone}</p></div>}
                      {showLeadDetail.email && <div><p className="text-xs text-slate-400">E-mail</p><p className="font-medium">{showLeadDetail.email}</p></div>}
                      {showLeadDetail.proxima_acao && <div><p className="text-xs text-slate-400">Proxima Acao</p><p className="font-medium">{showLeadDetail.proxima_acao}</p></div>}
                      {showLeadDetail.data_proxima_acao && <div><p className="text-xs text-slate-400">Data Proxima Acao</p><p className="font-medium">{new Date(showLeadDetail.data_proxima_acao).toLocaleDateString("pt-BR")}</p></div>}
                    </div>
                    {showLeadDetail.descricao && <div className="p-3 bg-slate-50 rounded text-sm"><p className="text-xs text-slate-400 mb-1">Descricao</p><p>{showLeadDetail.descricao}</p></div>}
                    {showLeadDetail.motivo_perda && <div className="p-3 bg-red-50 rounded text-sm"><p className="text-xs text-red-400 mb-1">Motivo da Perda</p><p className="text-red-600 font-medium">{showLeadDetail.motivo_perda}</p></div>}
                  </>
                ) : editForm && (
                  <div className="space-y-3 p-3 bg-emerald-50/50 border border-emerald-200 rounded-lg" data-testid="lead-edit-form">
                    <p className="text-xs text-emerald-700 font-medium uppercase tracking-wide">Editando lead - todos os campos disponiveis</p>
                    <div className="space-y-2">
                      <Label className="text-xs">Titulo *</Label>
                      <Input value={editForm.titulo} onChange={e => setEditForm(p => ({ ...p, titulo: e.target.value }))} data-testid="input-edit-titulo" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-xs">Cliente</Label>
                        <NativeSelect
                          value={editForm.cliente_id}
                          onValueChange={v => {
                            const c = clientes.find(x => x.id === v);
                            setEditForm(p => ({
                              ...p,
                              cliente_id: v,
                              cliente_nome: c?.nome || p.cliente_nome,
                              telefone: c?.telefone || p.telefone,
                              email: c?.email || p.email,
                            }));
                          }}
                          options={[{ value: "", label: "Sem cliente" }, ...clientes.map(c => ({ value: c.id, label: `${c.nome}${c.documento ? ` - ${c.documento}` : ""}` }))]}
                          data-testid="select-edit-cliente"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Responsavel</Label>
                        <NativeSelect
                          value={editForm.responsavel_id}
                          onValueChange={v => { const f = funcionarios.find(x => x.id === v); setEditForm(p => ({ ...p, responsavel_id: v, responsavel_nome: f?.nome || "" })); }}
                          options={[{ value: "", label: "Sem responsavel" }, ...funcionarios.map(f => ({ value: f.id, label: f.nome }))]}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-xs">Prioridade</Label>
                        <NativeSelect value={editForm.prioridade} onValueChange={v => setEditForm(p => ({ ...p, prioridade: v }))} options={[{ value: "baixa", label: "Baixa" }, { value: "media", label: "Media" }, { value: "alta", label: "Alta" }, { value: "urgente", label: "Urgente" }]} />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Valor Estimado (R$)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={editForm.valor_estimado}
                          onChange={e => setEditForm(p => ({ ...p, valor_estimado: e.target.value }))}
                          placeholder="0,00"
                          data-testid="input-edit-valor-estimado"
                        />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Origem</Label>
                      <NativeSelect
                        value={editForm.origem}
                        onValueChange={v => setEditForm(p => ({ ...p, origem: v, origem_outro_descricao: v === "outro" ? p.origem_outro_descricao : "" }))}
                        options={[{ value: "", label: "Selecione..." }, { value: "indicacao", label: "Indicacao" }, { value: "site", label: "Site" }, { value: "telefone", label: "Telefone" }, { value: "whatsapp", label: "WhatsApp" }, { value: "email", label: "E-mail" }, { value: "redes_sociais", label: "Redes Sociais" }, { value: "visita", label: "Visita" }, { value: "outro", label: "Outro" }]}
                        data-testid="select-edit-origem"
                      />
                    </div>
                    {editForm.origem === "outro" && (
                      <div className="space-y-2">
                        <Label className="text-xs">Descreva a origem *</Label>
                        <Input
                          value={editForm.origem_outro_descricao}
                          onChange={e => setEditForm(p => ({ ...p, origem_outro_descricao: e.target.value }))}
                          placeholder="Ex: Evento setorial, parceria com fornecedor X..."
                          data-testid="input-edit-origem-outro"
                        />
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-xs">Telefone</Label>
                        <Input value={editForm.telefone} onChange={e => setEditForm(p => ({ ...p, telefone: maskPhone(e.target.value) }))} placeholder="(00) 00000-0000" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">E-mail</Label>
                        <Input type="email" value={editForm.email} onChange={e => setEditForm(p => ({ ...p, email: e.target.value }))} />
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-xs">Proxima Acao</Label>
                        <Input value={editForm.proxima_acao} onChange={e => setEditForm(p => ({ ...p, proxima_acao: e.target.value }))} placeholder="Ex: Ligar para agendar" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs">Data Proxima Acao</Label>
                        <Input type="date" value={editForm.data_proxima_acao} onChange={e => setEditForm(p => ({ ...p, data_proxima_acao: e.target.value }))} />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Descricao</Label>
                      <Textarea value={editForm.descricao} onChange={e => setEditForm(p => ({ ...p, descricao: e.target.value }))} rows={3} />
                    </div>
                  </div>
                )}

                <Separator />

                {/* Add Activity */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-[#0A1628]">Registrar Atividade</p>
                  <div className="flex gap-2">
                    <NativeSelect value={newActivity.tipo} onValueChange={v => setNewActivity(p => ({ ...p, tipo: v }))} options={[
                      { value: "observacao", label: "Observacao" },
                      { value: "contato_telefonico", label: "Contato Telefonico" },
                      { value: "contato_whatsapp", label: "Contato WhatsApp" },
                      { value: "contato_email", label: "Contato E-mail" },
                      { value: "reuniao", label: "Reuniao" },
                      { value: "follow_up", label: "Follow-up" },
                      { value: "visita", label: "Visita Tecnica" },
                    ]} className="w-44" />
                    <Input value={newActivity.descricao} onChange={e => setNewActivity(p => ({ ...p, descricao: e.target.value }))} placeholder="Descreva a atividade..." className="flex-1" onKeyDown={e => e.key === "Enter" && handleAddActivity()} />
                    <Button type="button" onClick={handleAddActivity} size="sm" className="bg-[#0A1628]" data-testid="btn-add-atividade"><Send className="h-4 w-4" /></Button>
                  </div>
                </div>

                <Separator />

                {/* Timeline / History */}
                <div className="space-y-2">
                  <p className="text-sm font-medium text-[#0A1628]">Historico / Timeline</p>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {(showLeadDetail.historico || []).map((h, i) => (
                      <div key={h.id || i} className="flex gap-3 text-sm">
                        <div className="flex flex-col items-center">
                          <div className={`h-6 w-6 rounded-full flex items-center justify-center shrink-0 ${h.tipo === "mudanca_etapa" ? "bg-blue-100" : h.tipo === "criacao" ? "bg-emerald-100" : "bg-slate-100"}`}>
                            {h.tipo === "mudanca_etapa" ? <Target className="h-3 w-3 text-blue-600" /> : h.tipo === "criacao" ? <Plus className="h-3 w-3 text-emerald-600" /> : <MessageSquare className="h-3 w-3 text-slate-500" />}
                          </div>
                          {i < (showLeadDetail.historico || []).length - 1 && <div className="w-px flex-1 bg-slate-200 my-1" />}
                        </div>
                        <div className="flex-1 pb-3">
                          <p className="text-slate-700">{h.descricao}</p>
                          <p className="text-[10px] text-slate-400">{h.usuario_nome} - {h.created_at ? new Date(h.created_at).toLocaleString("pt-BR") : ""}</p>
                        </div>
                      </div>
                    ))}
                    {(!showLeadDetail.historico || showLeadDetail.historico.length === 0) && <p className="text-xs text-slate-400 text-center py-4">Nenhum registro</p>}
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ==================== ROUTER ====================
export default function CRMPage() {
  return (
    <MainLayout>
      <CRMKanban />
    </MainLayout>
  );
}
